// ============================================================================
// ROLE PERMISSION ENDPOINTS
// ============================================================================
// Endpoint definitions for the IAM Role Permissions module.
// Backend controller: crs.Api.Controllers.IamModule.RolePermissionsController
// (route token: [Route("api/v1/[controller]")] → /RolePermissions).

import { buildEndpoint, buildEndpointWithParams, buildEndpointWithQuery } from './base.endpoints';
import type { ID } from '../types';

const BASE_PATH = '/RolePermissions';

export const rolePermissionEndpoints = {
  /**
   * GET /RolePermissions (paginated, system-only)
   */
  list: () => buildEndpoint(BASE_PATH),

  /**
   * GET /RolePermissions/:id (system-only)
   */
  getById: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * GET /RolePermissions/known-roles
   *
   * Public endpoint called at app boot. Returns the current DB-driven role
   * catalog (roleCode + roleName + priority + isSystemRole) ordered priority
   * ASC. Cached client-side under STORAGE_KEYS.KNOWN_ROLES with a 15-minute
   * TTL — refreshed on cache expiry or on any 401 response.
   */
  knownRoles: () => buildEndpoint(`${BASE_PATH}/known-roles`),

  /**
   * GET /RolePermissions/by-roles?roles=crs_admin,crs_booking_agent
   *
   * Public endpoint — called during login before auth context is fully
   * established. Returns the merged visible_modules + module_actions for
   * the supplied role codes.
   *
   * Backend reads `roles` as a single comma/semicolon/space separated string
   * (RolePermissionsController.GetByRoles), so we serialise as one query
   * value rather than letting buildEndpointWithQuery expand the array into
   * repeated keys.
   */
  byRoles: (roles: ReadonlyArray<string>) =>
    buildEndpointWithQuery(`${BASE_PATH}/by-roles`, {
      roles: roles.filter((r) => r && r.trim().length > 0).join(','),
    }),

  /**
   * POST /RolePermissions
   */
  create: () => buildEndpoint(BASE_PATH),

  /**
   * PUT /RolePermissions/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * DELETE /RolePermissions/:id?rowVersion=N
   */
  delete: (id: ID, rowVersion: number) =>
    buildEndpointWithQuery(`${BASE_PATH}/${id}`, { rowVersion }),
} as const;

export default rolePermissionEndpoints;
